import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Suppress AbortError globally - these are expected from Supabase auth
const isAbortError = (error) => {
  if (!error) return false;
  const errorStr = String(error);
  const message = error?.message || '';
  const name = error?.name || '';
  return name === 'AbortError' ||
    message.includes('aborted') ||
    message.includes('signal is aborted') ||
    errorStr.includes('aborted') ||
    errorStr.includes('AbortError') ||
    // ResizeObserver errors
    message.includes('ResizeObserver loop') ||
    errorStr.includes('ResizeObserver loop');
};

// Hide React error overlay for AbortErrors in development
if (process.env.NODE_ENV === 'development') {
  // Monitor for error overlay iframe and hide it if it contains AbortError
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeName === 'IFRAME' && node.id === 'webpack-dev-server-client-overlay') {
          // Wait a bit for content to load, then check and hide if AbortError
          setTimeout(() => {
            try {
              const iframeDoc = node.contentDocument || node.contentWindow?.document;
              if (iframeDoc) {
                const text = iframeDoc.body?.innerText || '';
                if (text.includes('AbortError') || text.includes('signal is aborted') || text.includes('ResizeObserver loop')) {
                  node.style.display = 'none';
                  node.remove();
                }
              }
            } catch (e) {
              // Cross-origin issues, try removing by checking parent
            }
          }, 100);
        }
        // Also check for react-error-overlay
        if (node.id === 'webpack-dev-server-client-overlay-div' ||
          (node.style && node.style.position === 'fixed' && node.innerHTML?.includes('AbortError'))) {
          node.style.display = 'none';
          node.remove();
        }
      });
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // Override reportError if it exists
  if (typeof window.reportError === 'function') {
    const originalReportError = window.reportError;
    window.reportError = (error) => {
      if (!isAbortError(error)) {
        originalReportError(error);
      }
    };
  }
}

// Capture phase handlers to intercept before React
window.addEventListener('unhandledrejection', (event) => {
  if (isAbortError(event.reason)) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return false;
  }
}, true);

window.addEventListener('error', (event) => {
  if (isAbortError(event.error) || (event.message && event.message.includes('aborted'))) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return false;
  }
}, true);

// Override console.error to suppress abort errors
const originalConsoleError = console.error;
console.error = (...args) => {
  const errorString = args.map(a => String(a)).join(' ');
  if (errorString.includes('signal is aborted') ||
    errorString.includes('AbortError') ||
    errorString.includes('aborted without reason') ||
    errorString.includes('ResizeObserver loop')) {
    return;
  }
  originalConsoleError.apply(console, args);
};

// Global error handler
window.onerror = function (message, source, lineno, colno, error) {
  if (isAbortError(error) || (message && String(message).includes('aborted'))) {
    return true;
  }
  return false;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <AuthProvider>
        <App />
      </AuthProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
